import { useState } from "react";
import {
  Table, Button, Input, Select, Tag, Card, Modal, message, Popconfirm, Space, Drawer,
  Descriptions, InputNumber, Form, Divider, Switch,
} from "antd";
import { Plus, Search, Power, Trash2, Clock, Download, Eye, Pencil } from "lucide-react";
import {
  useCompanyList, useCompanyToggleActive, useCompanyExtendTrial, useCompanyDelete, useCompanyChangePlan,
  useCompanyUpdate,
} from "../../../api-hooks/companies";
import { usePlanList } from "../../../api-hooks/plans";
import { TCompany } from "../../../types";
import CreateCompanyWizard from "./CreateCompanyWizard";
import dayjs from "dayjs";

const statusColor: Record<string, string> = {
  active: "green", trial: "blue", past_due: "orange", cancelled: "red", paused: "default",
};

function exportCsv(companies: TCompany[]) {
  const headers = ["ID", "Name", "Email", "Plan", "Status", "Users", "City", "Created"];
  const rows = companies.map((c) => [
    c.id, c.name, c.email, c.plan_name ?? "", c.subscription_status,
    c.user_count ?? 0, c.city ?? "", dayjs(c.created_at).format("YYYY-MM-DD"),
  ]);
  const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `companies-${dayjs().format("YYYY-MM-DD")}.csv`;
  a.click();
}

export default function Companies() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [showWizard, setShowWizard] = useState(false);
  const [detailCompany, setDetailCompany] = useState<TCompany | null>(null);
  const [trialCompany, setTrialCompany] = useState<TCompany | null>(null);
  const [trialDays, setTrialDays] = useState(14);
  const [changePlanCompany, setChangePlanCompany] = useState<TCompany | null>(null);
  const [newPlanId, setNewPlanId] = useState<number | null>(null);
  const [editCompany, setEditCompany] = useState<TCompany | null>(null);
  const [editForm] = Form.useForm();
  const [messageApi, ctx] = message.useMessage();

  const { data: companies = [], isLoading } = useCompanyList({
    key: search || undefined,
    status: statusFilter || undefined,
    limit: 200,
  });

  const { mutate: toggleActive } = useCompanyToggleActive({
    onSuccess: () => messageApi.success("Status updated."),
    onError: () => messageApi.error("Failed."),
  });

  const { mutate: extendTrial, isPending: extending } = useCompanyExtendTrial({
    onSuccess: () => { messageApi.success("Trial extended."); setTrialCompany(null); },
    onError: (e: any) => messageApi.error(e?.message || "Failed."),
  });

  const { data: plans = [] } = usePlanList();

  const { mutate: changePlan, isPending: changingPlan } = useCompanyChangePlan({
    onSuccess: () => { messageApi.success("Plan changed."); setChangePlanCompany(null); setNewPlanId(null); },
    onError: () => messageApi.error("Failed to change plan."),
  });

  const { mutate: deleteCompany } = useCompanyDelete({
    onSuccess: () => messageApi.success("Company deleted."),
    onError: () => messageApi.error("Failed to delete."),
  });

  const { mutate: updateCompany, isPending: updating } = useCompanyUpdate({
    onSuccess: () => { messageApi.success("Company updated."); setEditCompany(null); editForm.resetFields(); },
    onError: (e: any) => messageApi.error(e?.message || "Update failed."),
  });

  const columns = [
    {
      title: "Company",
      dataIndex: "name",
      key: "name",
      render: (name: string, row: TCompany) => (
        <button
          className="text-left hover:text-violet-700 transition-colors"
          onClick={() => setDetailCompany(row)}
        >
          <div className="font-medium text-slate-800 flex items-center gap-1">
            {name} <Eye size={12} className="text-slate-400" />
          </div>
          <div className="text-xs text-slate-500">{row.email}</div>
        </button>
      ),
    },
    { title: "Plan", dataIndex: "plan_name", key: "plan_name", render: (p: string) => <Tag>{p ?? "—"}</Tag> },
    {
      title: "Status",
      dataIndex: "subscription_status",
      key: "subscription_status",
      render: (s: string) => <Tag color={statusColor[s] || "default"}>{s?.replace("_", " ")?.toUpperCase()}</Tag>,
    },
    {
  title: "Trial Ends",
  dataIndex: "subscription_ends_at",
  key: "subscription_ends_at",
  render: (d: string | null, row: TCompany) =>
    row.subscription_status === "trial" && d ? (
      <span
        className={`text-xs ${
          dayjs(d).isBefore(dayjs())
            ? "text-red-500"
            : "text-slate-600"
        }`}
      >
        {dayjs(d).format("DD MMM YY")}
      </span>
    ) : (
      <span className="text-xs text-slate-400">—</span>
    ),
},
    { title: "Users", dataIndex: "user_count", key: "user_count", width: 70 },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 110,
      render: (d: string) => dayjs(d).format("DD MMM YYYY"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 220,
      render: (_: any, row: TCompany) => (
        <Space size={4}>
          <Popconfirm
            title={`${row.is_active ? "Suspend" : "Activate"} this company?`}
            onConfirm={() => toggleActive({ id: row.id, is_active: !row.is_active })}
          >
            <Button size="small" icon={<Power size={11} />} danger={row.is_active}>
              {row.is_active ? "Suspend" : "Activate"}
            </Button>
          </Popconfirm>
          {row.subscription_status === "trial" && (
            <Button
              size="small"
              icon={<Clock size={11} />}
              onClick={() => { setTrialCompany(row); setTrialDays(14); }}
            >
              Extend
            </Button>
          )}
          <Popconfirm
            title="Delete company? This cannot be undone."
            onConfirm={() => deleteCompany(row.id)}
          >
            <Button size="small" icon={<Trash2 size={11} />} danger type="text" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {ctx}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Companies</h2>
          <p className="text-slate-500 text-sm mt-0.5">{companies.length} total</p>
        </div>
        <Space>
          <Button icon={<Download size={14} />} onClick={() => exportCsv(companies)} disabled={companies.length === 0}>
            Export CSV
          </Button>
          <Button
            type="primary"
            icon={<Plus size={14} />}
            onClick={() => setShowWizard(true)}
            style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
          >
            New Company
          </Button>
        </Space>
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm mb-4">
        <div className="flex gap-3">
          <Input
            prefix={<Search size={14} className="text-slate-400" />}
            placeholder="Search companies..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-lg"
            style={{ maxWidth: 320 }}
            allowClear
          />
          <Select
            placeholder="Filter by status"
            value={statusFilter || undefined}
            onChange={(v) => setStatusFilter(v || "")}
            allowClear
            style={{ width: 160 }}
            options={[
              { value: "trial", label: "Trial" },
              { value: "active", label: "Active" },
              { value: "past_due", label: "Past Due" },
              { value: "cancelled", label: "Cancelled" },
              { value: "paused", label: "Paused" },
            ]}
          />
        </div>
      </Card>

      <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={companies}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 20, showSizeChanger: false, showTotal: (t) => `${t} companies` }}
        />
      </Card>

      {/* Create Company Modal */}
      <Modal
        open={showWizard}
        onCancel={() => setShowWizard(false)}
        footer={null}
        width={760}
        title="Create New Company"
        destroyOnClose
      >
        <CreateCompanyWizard onSuccess={() => setShowWizard(false)} onCancel={() => setShowWizard(false)} />
      </Modal>

      {/* Company Detail Drawer */}
      <Drawer
        open={!!detailCompany}
        onClose={() => setDetailCompany(null)}
        title={detailCompany?.name ?? "Company Details"}
        width={520}
      >
        {detailCompany && (
          <div className="space-y-5">
            {/* Profile */}
            <div>
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Company Profile</div>
              <Descriptions column={1} bordered size="small" labelStyle={{ fontWeight: 600, width: 140 }}>
                <Descriptions.Item label="ID">#{detailCompany.id}</Descriptions.Item>
                <Descriptions.Item label="Name">{detailCompany.name}</Descriptions.Item>
                <Descriptions.Item label="Slug"><code className="text-xs">{detailCompany.slug}</code></Descriptions.Item>
                <Descriptions.Item label="Email">{detailCompany.email}</Descriptions.Item>
                <Descriptions.Item label="Phone">{detailCompany.phone ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Website">{detailCompany.website ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Reg. No.">{detailCompany.company_registration_number ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="VAT No.">{detailCompany.vat_number ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="VAT Registered">{detailCompany.vat_registered ? "Yes" : "No"}</Descriptions.Item>
                <Descriptions.Item label="Address">
                  {[detailCompany.address_line1, detailCompany.address_line2, detailCompany.city, detailCompany.county, detailCompany.postcode, detailCompany.country].filter(Boolean).join(", ") || "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Users">{detailCompany.user_count ?? 0}</Descriptions.Item>
                <Descriptions.Item label="Active">
                  <Tag color={detailCompany.is_active ? "green" : "red"}>{detailCompany.is_active ? "Yes" : "No"}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Industries">
                  <div className="flex flex-wrap gap-1">
                    {detailCompany.industries?.map((i) => (
                      <Tag key={i.id} color={i.is_primary ? "purple" : "default"}>{i.name}</Tag>
                    )) ?? <span className="text-slate-400">—</span>}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                  {dayjs(detailCompany.created_at).format("DD MMM YYYY HH:mm")}
                </Descriptions.Item>
              </Descriptions>
            </div>

            {/* Subscription */}
            <div>
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Subscription</div>
              <Descriptions column={1} bordered size="small" labelStyle={{ fontWeight: 600, width: 140 }}>
<Descriptions.Item label="Plan">
  <Tag>{detailCompany.plan_name ?? "—"}</Tag>
</Descriptions.Item>

<Descriptions.Item label="Status">
  {detailCompany.subscription_status ? (
    <Tag color={statusColor[detailCompany.subscription_status] || "default"}>
      {detailCompany.subscription_status.replace("_", " ").toUpperCase()}
    </Tag>
  ) : (
    "—"
  )}
</Descriptions.Item>

<Descriptions.Item label="Started">
  {detailCompany.subscription_started_at
    ? dayjs(detailCompany.subscription_started_at).format("DD MMM YYYY")
    : "—"}
</Descriptions.Item>

<Descriptions.Item label="Ends / Trial">
  {detailCompany.subscription_ends_at
    ? dayjs(detailCompany.subscription_ends_at).format("DD MMM YYYY")
    : "—"}
</Descriptions.Item>

<Descriptions.Item label="Amount">
  {detailCompany.subscription_amount
    ? `£${Number(detailCompany.subscription_amount).toFixed(2)}`
    : "—"}
</Descriptions.Item>
              </Descriptions>
            </div>

            {/* Financial & Format Settings */}
            <div>
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Financial & Format Settings</div>
              <Descriptions column={1} bordered size="small" labelStyle={{ fontWeight: 600, width: 160 }}>
                <Descriptions.Item label="Currency">{detailCompany.currency ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Default VAT Rate">
                  {detailCompany.default_vat_rate != null ? `${detailCompany.default_vat_rate}%` : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Payment Terms">
                  {detailCompany.default_payment_terms_days != null ? `${detailCompany.default_payment_terms_days} days` : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Fin. Year Start">
                  {detailCompany.financial_year_start_month
                    ? dayjs().month(detailCompany.financial_year_start_month - 1).format("MMMM")
                    : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Invoice Format">{detailCompany.invoice_number_format ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Quote Format">{detailCompany.quote_number_format ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Job Format">{detailCompany.job_number_format ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="PO Format">{detailCompany.po_number_format ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Timezone">{detailCompany.timezone ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Date Format">{detailCompany.date_format ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Working Hours">
                  {detailCompany.working_hours_start && detailCompany.working_hours_end
                    ? `${detailCompany.working_hours_start} – ${detailCompany.working_hours_end}`
                    : "—"}
                </Descriptions.Item>
              </Descriptions>
            </div>

            {/* Actions */}
            <div>
              <Divider orientation="left" className="text-xs text-slate-400">Actions</Divider>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="small"
                  icon={<Pencil size={12} />}
                  type="primary"
                  style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
                  onClick={() => { setEditCompany(detailCompany); editForm.setFieldsValue({ ...detailCompany }); setDetailCompany(null); }}
                >
                  Edit Company
                </Button>
                <Button size="small" onClick={() => { setChangePlanCompany(detailCompany); setNewPlanId(detailCompany.plan_id ?? null); }}>
                  Change Plan
                </Button>
                {detailCompany.subscription_status === "trial" && (
                  <Button size="small" onClick={() => { setTrialCompany(detailCompany); setTrialDays(14); }}>
                    Extend Trial
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </Drawer>

      {/* Change Plan Modal */}
      <Modal
        open={!!changePlanCompany}
        onCancel={() => setChangePlanCompany(null)}
        title={`Change Plan — ${changePlanCompany?.name}`}
        onOk={() => changePlanCompany && newPlanId && changePlan({ id: changePlanCompany.id, plan_id: newPlanId })}
        okText="Change Plan"
        confirmLoading={changingPlan}
        okButtonProps={{ disabled: !newPlanId || newPlanId === changePlanCompany?.plan_id, style: { background: "#4c1d95", borderColor: "#4c1d95" } }}
      >
        <p className="text-slate-600 mb-4">Current plan: <strong>{changePlanCompany?.plan_name ?? "—"}</strong></p>
        <Select
          value={newPlanId ?? undefined}
          onChange={(v) => setNewPlanId(v)}
          style={{ width: "100%" }}
          placeholder="Select new plan"
          options={plans.map((p: any) => ({ value: p.id, label: `${p.name} — £${Number(p.price_monthly).toFixed(0)}/mo` }))}
        />
      </Modal>

      {/* Extend Trial Modal */}
      <Modal
        open={!!trialCompany}
        onCancel={() => setTrialCompany(null)}
        title={`Extend Trial — ${trialCompany?.name}`}
        onOk={() => trialCompany && extendTrial({ id: trialCompany.id, days: trialDays })}
        okText="Extend Trial"
        confirmLoading={extending}
        okButtonProps={{ style: { background: "#4c1d95", borderColor: "#4c1d95" } }}
      >
        <p className="text-slate-600 mb-4">How many additional days to extend the trial?</p>
        <InputNumber
          value={trialDays}
          onChange={(v) => setTrialDays(v ?? 14)}
          min={1}
          max={90}
          addonAfter="days"
          className="w-full"
        />
        <p className="text-xs text-slate-400 mt-2">Maximum 90 days extension.</p>
      </Modal>

      {/* Edit Company Modal */}
      <Modal
        open={!!editCompany}
        onCancel={() => { setEditCompany(null); editForm.resetFields(); }}
        title={`Edit Company — ${editCompany?.name}`}
        footer={null}
        width={780}
        destroyOnClose
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(vals) => editCompany && updateCompany({ id: editCompany.id, data: vals })}
          className="mt-2"
        >
          {/* Profile */}
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">Company Profile</div>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="name" label="Company Name" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="email" label="Contact Email" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="phone" label="Phone"><Input /></Form.Item>
            <Form.Item name="website" label="Website"><Input /></Form.Item>
            <Form.Item name="company_registration_number" label="Reg. Number"><Input /></Form.Item>
            <Form.Item name="trading_name" label="Trading Name"><Input /></Form.Item>
          </div>

          {/* Address */}
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3 mt-1">Address</div>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="address_line1" label="Address Line 1" className="col-span-2"><Input /></Form.Item>
            <Form.Item name="address_line2" label="Address Line 2" className="col-span-2"><Input /></Form.Item>
            <Form.Item name="city" label="City"><Input /></Form.Item>
            <Form.Item name="county" label="County"><Input /></Form.Item>
            <Form.Item name="postcode" label="Postcode"><Input /></Form.Item>
            <Form.Item name="country" label="Country"><Input /></Form.Item>
          </div>

          {/* VAT / Financial */}
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3 mt-1">VAT & Financial</div>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="vat_number" label="VAT Number"><Input /></Form.Item>
            <Form.Item name="vat_registered" label="VAT Registered" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="default_vat_rate" label="Default VAT Rate (%)">
              <InputNumber min={0} max={100} step={0.5} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="vat_scheme" label="VAT Scheme">
              <Select options={[
                { value: "standard", label: "Standard" },
                { value: "flat_rate", label: "Flat Rate" },
                { value: "cash_accounting", label: "Cash Accounting" },
                { value: "annual", label: "Annual" },
              ]} />
            </Form.Item>
            <Form.Item name="default_payment_terms_days" label="Payment Terms (days)">
              <InputNumber min={0} max={365} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="financial_year_start_month" label="Fin. Year Start Month">
              <Select options={Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: dayjs().month(i).format("MMMM") }))} />
            </Form.Item>
            <Form.Item name="currency" label="Currency">
              <Select options={[
                { value: "GBP", label: "GBP £" },
                { value: "EUR", label: "EUR €" },
                { value: "USD", label: "USD $" },
              ]} />
            </Form.Item>
            <Form.Item name="late_interest_rate" label="Late Interest Rate (%)">
              <InputNumber min={0} max={100} step={0.01} style={{ width: "100%" }} />
            </Form.Item>
          </div>

          {/* Number Formats */}
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3 mt-1">Number Formats</div>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="invoice_number_format" label="Invoice Format">
              <Input placeholder="INV-{YEAR}-{SEQ}" />
            </Form.Item>
            <Form.Item name="quote_number_format" label="Quote Format">
              <Input placeholder="QUO-{YEAR}-{SEQ}" />
            </Form.Item>
            <Form.Item name="job_number_format" label="Job Format">
              <Input placeholder="JOB-{YEAR}-{SEQ}" />
            </Form.Item>
            <Form.Item name="po_number_format" label="PO Format">
              <Input placeholder="PO-{YEAR}-{SEQ}" />
            </Form.Item>
          </div>

          {/* Regional */}
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3 mt-1">Regional</div>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="timezone" label="Timezone">
              <Select showSearch options={[
                { value: "Europe/London", label: "Europe/London" },
                { value: "Europe/Dublin", label: "Europe/Dublin" },
                { value: "UTC", label: "UTC" },
              ]} />
            </Form.Item>
            <Form.Item name="date_format" label="Date Format">
              <Select options={[
                { value: "DD/MM/YYYY", label: "DD/MM/YYYY" },
                { value: "MM/DD/YYYY", label: "MM/DD/YYYY" },
                { value: "YYYY-MM-DD", label: "YYYY-MM-DD" },
              ]} />
            </Form.Item>
            <Form.Item name="working_hours_start" label="Working Hours Start">
              <Input placeholder="08:00:00" />
            </Form.Item>
            <Form.Item name="working_hours_end" label="Working Hours End">
              <Input placeholder="17:30:00" />
            </Form.Item>
          </div>

          {/* CIS */}
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3 mt-1">CIS / Tax</div>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="cis_contractor" label="CIS Contractor" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="utr_number" label="UTR Number"><Input /></Form.Item>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={() => { setEditCompany(null); editForm.resetFields(); }}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={updating}
              style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
            >
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
